import { useEffect, useState } from "react";
import { usePixiBillingClient } from "../billing/usePixiBillingClient";
import {
  ALL_CURRENCY_CODES,
  type CurrencyCode,
} from "../steps/PlanBillingStep/constants";

export interface UseTrialistCurrency {
  /** The trialist's locked billing currency, or undefined until resolved. */
  currency?: CurrencyCode;
  loading: boolean;
  /** True when the currency could not be resolved — the flow must block. */
  error: boolean;
}

/**
 * Resolves the trialist's billing currency from their existing plan. Chargebee
 * locks a customer to one currency, so this — not a user selection — is the
 * single source of truth for the whole Pixi purchase. The wizard
 * seeds its currency from this and the selector is read-only; if it can't be
 * resolved the flow blocks rather than guessing a currency the customer never
 * agreed to.
 */
export const useTrialistCurrency = (): UseTrialistCurrency => {
  const billing = usePixiBillingClient();
  const [currency, setCurrency] = useState<CurrencyCode>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // Waits for the billing version; loading stays true meanwhile.
    if (!billing) {
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(false);
      // getPlan swallows errors and returns {} — so an empty/invalid currency
      // is treated as "unresolved" and blocks the flow.
      const code = await billing.getCurrency();
      if (cancelled) return;
      if (code && (ALL_CURRENCY_CODES as string[]).includes(code)) {
        setCurrency(code as CurrencyCode);
      } else {
        setError(true);
      }
      setLoading(false);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [billing]);

  return { currency, loading, error };
};
